import * as React from "react";
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
  LocalStorageEntities,
} from "../../../../utils/local-storage";
import { GlobalContext } from "../../../../global-state";

type StatusFilterEntities = Pick<
  LocalStorageEntities,
  // LocalStorage へ保存するもののうち、このファイルで定義した hook が
  // アクセスするものをピックアップ。
  | "ApplyStatusFilter"
  | "ProcManagementStatusFilter"
  | "ProcedureStatusFilter"
  | "SurveyStatusFilter"
>;
type storageKey = keyof LocalStorageEntities;

type Props<K extends storageKey> = {
  storageKey: K;
} & (
  | {
      // この hook を呼び出す際の初期値。
      // ローカルストレージで保存しているデータも
      // 上書きして適用されます。
      preferredStatuses: StatusFilterEntities[K];
    }
  | {
      // preferredStatuses が指定されず、
      // localStorage のデータも存在しない場合、
      // defaultStatuses へフォールバックします。
      defaultStatuses: StatusFilterEntities[K];
    }
);

export const useStatusFilterState = <K extends storageKey>(props: Props<K>) => {
  const { storageKey } = props;

  const [status, setStatus] = React.useState(() => {
    if ("preferredStatuses" in props) {
      return props.preferredStatuses;
    }
    return getLocalStorageItem(storageKey) ?? props.defaultStatuses;
  });
  const [storageFilterEnabled, setStorageFilterEnabled] = React.useState(
    !!getLocalStorageItem(storageKey),
  );

  React.useEffect(() => {
    if (storageFilterEnabled) {
      setLocalStorageItem(storageKey, status);
    } else {
      removeLocalStorageItem(storageKey);
    }
  }, [storageKey, storageFilterEnabled, status]);

  const { isLocalStorageEnabled } = React.useContext(GlobalContext);

  return {
    status,
    setStatus,
    storageFilterEnabled,
    setStorageFilterEnabled,
    isLocalStorageEnabled,
  };
};
